import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  Check,
  ChevronDown,
  Download,
  FileText,
  Folder,
  Plus,
  X,
} from "react-feather";
import {
  Banner,
  Button,
  Checkbox,
  ClickOutside,
  Input,
  Select,
  SelectOption,
  Spinner,
} from "@cinesys/ui/components";
import { Chip, SubLabel } from "./ui";
import { matchStrength, rankFields } from "./fieldRanking";
import {
  FieldDescriptor,
  useAssetTitle,
  collectionRoots,
  useCollectionTitle,
  useFieldCatalog,
  useProfileConfig,
  useSearchAssets,
  useSearchCollections,
  useSourceCollections,
  useSourceColumns,
  useUpdateProfile,
  WireProfile,
} from "./service";
import { useSaveSection } from "./saveController";
import { MappingRowState, resolveRow } from "./fieldRowState";
import { detectSourceFormat, FORMAT_LABELS } from "./sourceFormat";
import {
  buildCsvTemplate,
  buildFieldReferenceCsv,
  downloadCsv,
  FieldRefRow,
} from "./templates";

/* ---------------------------------------------------------------------------------------------
 * Section content primitives. Each editor renders directly inside its section-card body (the card
 * owns the surface + consistent inset) — so there are no nested boxes here. A genuine sub-group is a
 * quiet titled divider (`SubLabel`), never another bordered panel. Persistence is lifted to the
 * workflow-level Save (`useSaveSection`); no per-section Save buttons live here.
 * ------------------------------------------------------------------------------------------- */
const Field = styled.div`
  margin-bottom: 18px;
  max-width: 680px;
  &:last-child {
    margin-bottom: 0;
  }
`;
const Help = styled.p`
  margin: 7px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--miInkFaint);
  max-width: 74ch;
`;
const Results = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 10px;
  max-height: 240px;
  overflow: auto;
`;
/** Field-picker dropdown: floated as an overlay so its (often long) option text never widens the
 *  table column and reflows the row. */
const PickerMenu = styled(Results)`
  margin-top: 0;
  padding: 5px;
  background: var(--miSurface);
  border: 1px solid var(--miLine);
  border-radius: 10px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.5);
  button {
    overflow-wrap: anywhere;
  }
`;
const Pick = styled.button<{ $on?: boolean }>`
  text-align: left;
  background: ${(p) => (p.$on ? "var(--miAccentBright)" : "var(--miRaised)")};
  color: ${(p) => (p.$on ? "#052e1a" : "var(--miInkSoft)")};
  border: 1px solid
    ${(p) => (p.$on ? "var(--miAccentBright)" : "var(--miLine)")};
  border-radius: 8px;
  padding: 9px 12px;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  &:hover {
    border-color: var(--miAccentBright);
  }
`;
const Selected = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: var(--miInkSoft);
  margin-bottom: 16px;

  strong {
    color: #fff;
    font-weight: 600;
  }
`;
/** A source that's been picked but not yet loaded — a blue-tinted callout with the Load action. */
const PendingSource = styled.div`
  margin: 12px 0 16px;
  padding: 12px 14px;
  border: 1px solid var(--miAccentLine);
  border-left: 3px solid var(--miAccent);
  background: var(--miAccentSoft);
  border-radius: 12px;

  .row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .row svg {
    color: var(--miAccentBright);
    flex: none;
  }
  .row strong {
    color: #fff;
    font-weight: 600;
  }
  .parser {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font: inherit;
    font-size: 12.5px;
    color: var(--miAccentBright);
  }
`;

/** A single quiet inline action at the foot of a section (e.g. a download) — a ghost button on its
 *  own row, never wrapped in an "Actions" box. */
const InlineAction = styled.div`
  margin-top: 20px;
`;
const HtmlTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    text-align: left;
    padding: 8px 12px;
    border-bottom: 1px solid var(--miLine);
    vertical-align: top;
    color: var(--miInkSoft);
  }
  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--miSurface);
    color: var(--miInkFaint);
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding-top: 4px;
  }
  tr:last-child td {
    border-bottom: none;
  }
`;

/** Bounds the mapping list to ~6–7 rows before it scrolls internally, so a wide source (tens or
 *  hundreds of columns) can't make the page dozens of screens tall. */
const MapScroll = styled.div`
  max-height: 460px;
  overflow-y: auto;
  border: 1px solid var(--miLine);
  border-radius: 10px;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  .box {
    flex: 1 1 auto;
    max-width: 420px;
  }
  .hint {
    font-size: 12.5px;
    color: var(--miInkFaint);
  }
`;

/**
 * Anchors an overlay menu to a trigger with `position: fixed` (viewport-relative), so it escapes the
 * `overflow` clipping of the bounded mapping viewport / source card WITHOUT a portal — the menu stays
 * a DOM descendant (so ClickOutside still sees clicks as "inside"). Tracks the trigger on scroll
 * (capture = also inner scroll containers) and resize. Opens below the control by default; flips
 * above when there isn't room below.
 */
function useAnchoredPosition(open: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ display: "none" });
  useLayoutEffect(() => {
    if (!open || !ref.current) {
      return;
    }
    const el = ref.current;
    const update = () => {
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom;
      const openUp = below < 260 && r.top > below;
      setStyle({
        position: "fixed",
        left: r.left,
        width: r.width,
        zIndex: 1000,
        ...(openUp
          ? { bottom: window.innerHeight - r.top + 4 }
          : { top: r.bottom + 4 }),
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);
  return { ref, style };
}

function Loading({ q }: { q: ReturnType<typeof useProfileConfig> }) {
  if (q.isLoading) {
    return <Spinner padTop />;
  }
  if (q.isError || !q.data) {
    return <Banner error="Could not load this workflow's configuration." />;
  }
  return null;
}

const stableAdapter = (a: Record<string, unknown> | undefined): string =>
  JSON.stringify(a ?? {});

/* --------------------------------------------------------------- SOURCE editor */
export function SourceEditor({ id }: { id: string }) {
  const cfg = useProfileConfig(id);
  const update = useUpdateProfile(id);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [assetId, setAssetId] = useState<string>();
  const [format, setFormat] = useState<string>();
  const [adapterCfg, setAdapterCfg] = useState<Record<string, unknown>>();
  const [showParser, setShowParser] = useState(false);
  const srcAnchor = useAnchoredPosition(open);
  const assets = useSearchAssets(q);
  const chosen = assetId ?? cfg.data?.source.assetId ?? "";
  const fmt = format ?? cfg.data?.source.format ?? "csv";
  const cfgObj = adapterCfg ?? cfg.data?.source.adapterConfig ?? {};
  const columns = useSourceColumns(chosen || undefined, fmt, cfgObj);
  const title = useAssetTitle(chosen || undefined);

  const saved = cfg.data?.source;
  const dirty =
    !!saved &&
    (chosen !== saved.assetId ||
      fmt !== saved.format ||
      stableAdapter(cfgObj) !== stableAdapter(saved.adapterConfig));
  useSaveSection("source", {
    order: 1,
    label: "Metadata source",
    dirty,
    validate: () => (chosen ? null : "Select a source asset before saving."),
    getPatch: () => ({
      // `valueDelimiter` is owned by Field Mappings; the Save controller backfills it from config.
      source: { format: fmt, assetId: chosen, adapterConfig: cfgObj },
    }),
  });

  const loading = Loading({ q: cfg });
  if (loading) {
    return loading;
  }

  const detection = columns.data?.detection;
  const setCfg = (patch: Record<string, unknown>) =>
    setAdapterCfg({ ...cfgObj, ...patch });
  const changeFormat = (f: string) => {
    setFormat(f);
    setAdapterCfg({}); // structural config is per-format; reset when the format changes
  };
  const s = (v: unknown): string => (v == null ? "" : String(v));
  const sourceTitle = title.data?.title || (chosen ? chosen : "");
  // `loaded` = the chosen source matches the saved (authoritative) config; `pending` = a selection
  // not yet loaded. Load source persists just the source so downstream sections populate.
  const loaded = !!chosen && !dirty;
  const pending = !!chosen && dirty;
  const loadSource = () =>
    update.mutate({
      source: { format: fmt, assetId: chosen, adapterConfig: cfgObj },
    });

  return (
    <>
      {/* The LOADED (authoritative, persisted) source. Distinct from a merely-pending selection
          below — this only shows once the source has actually been loaded into the config. */}
      {loaded && (
        <Selected title={`iconik asset ${chosen}`}>
          <FileText size={17} style={{ color: "var(--miAccent)" }} />
          <strong>{sourceTitle}</strong>
          <Chip>{FORMAT_LABELS[fmt] ?? fmt.toUpperCase()}</Chip>
          <button
            type="button"
            onClick={() => setShowParser((v) => !v)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              font: "inherit",
              fontSize: 12.5,
              color: "var(--miAccentBright)",
            }}
          >
            {showParser ? "Hide parser" : "Change parser"}
          </button>
        </Selected>
      )}
      <Field>
        <ClickOutside onClickOutside={() => setOpen(false)}>
          <div ref={srcAnchor.ref} style={{ position: "relative" }}>
            <div onClick={() => setOpen(true)}>
              <Input
                label={loaded ? "Change source asset" : "Metadata source"}
                value={q}
                onChange={(v: string) => {
                  setQ(v);
                  setOpen(true);
                }}
                placeholder="Search metadata files…"
                clearable
              />
            </div>
            {open &&
              (() => {
                const recent = q.trim() === "";
                const items = assets.data ?? [];
                if (assets.isFetching && items.length === 0) {
                  return (
                    <PickerMenu style={srcAnchor.style}>
                      <div style={{ padding: "6px 8px", fontSize: 12.5 }}>
                        {recent ? "Loading…" : "Searching…"}
                      </div>
                    </PickerMenu>
                  );
                }
                if (items.length === 0) {
                  return (
                    <PickerMenu style={srcAnchor.style}>
                      <div
                        style={{
                          padding: "6px 8px",
                          fontSize: 12.5,
                          color: "var(--miInkFaint)",
                        }}
                      >
                        {recent
                          ? "No recent metadata source files — type to search."
                          : "No matching metadata files."}
                      </div>
                    </PickerMenu>
                  );
                }
                return (
                  <PickerMenu style={srcAnchor.style}>
                    {recent && (
                      <div
                        style={{
                          fontSize: 11,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          color: "var(--miInkFaint)",
                          padding: "2px 2px 6px",
                        }}
                      >
                        Recent metadata files
                      </div>
                    )}
                    {items.map((a) => {
                      const det = detectSourceFormat(a.title);
                      return (
                        <Pick
                          key={a.id}
                          $on={a.id === chosen}
                          onClick={() => {
                            setAssetId(a.id);
                            if (det) {
                              setFormat(det);
                              setAdapterCfg({});
                              setShowParser(false);
                            } else {
                              setShowParser(true);
                            }
                            setQ("");
                            setOpen(false);
                          }}
                        >
                          {a.title}
                          {det && (
                            <span style={{ opacity: 0.6 }}>
                              {" "}
                              · {FORMAT_LABELS[det]}
                            </span>
                          )}
                        </Pick>
                      );
                    })}
                  </PickerMenu>
                );
              })()}
          </div>
        </ClickOutside>
      </Field>

      {/* PENDING selection — chosen but not yet loaded into the config. Load source persists just the
          source so its schema/preview/fields populate without needing the global workflow Save. */}
      {pending && (
        <PendingSource>
          <div className="row">
            <FileText size={16} />
            <strong>{sourceTitle}</strong>
            <Chip>{FORMAT_LABELS[fmt] ?? fmt.toUpperCase()}</Chip>
            <button
              type="button"
              className="parser"
              onClick={() => setShowParser((v) => !v)}
            >
              {showParser ? "Hide parser" : "Change parser"}
            </button>
            <Button
              label={update.isLoading ? "Loading…" : "Load source"}
              type="success"
              disabled={update.isLoading}
              onClick={loadSource}
            />
          </div>
          <Help style={{ marginTop: 8 }}>
            Load this file to use it as the workflow source — its fields and
            preview populate and Field Mappings becomes editable.
          </Help>
        </PendingSource>
      )}

      {chosen && showParser && (
        <Field>
          <Select
            label="Parser format"
            options={[
              { value: "csv", label: "CSV" },
              { value: "xlsx", label: "XLSX (Excel workbook)" },
              { value: "json", label: "JSON" },
              { value: "xml", label: "XML" },
            ]}
            value={fmt}
            onChange={(o) =>
              changeFormat(((o as SelectOption)?.value as string) ?? "csv")
            }
          />
          <Help>
            Detected from the file. Change it only if the file was parsed with
            the wrong parser.
          </Help>
        </Field>
      )}

      {chosen && fmt === "xlsx" && (
        <>
          <SubLabel>Workbook structure</SubLabel>
          {detection?.error && (
            <div style={{ marginBottom: 12 }}>
              <Banner error={s(detection.error)} />
            </div>
          )}
          <Field>
            <Select
              label="Worksheet"
              options={(detection?.sheetNames ?? []).map((n) => ({
                value: n,
                label: n,
              }))}
              value={s(
                cfgObj.worksheet ??
                  detection?.sheetNames?.[
                    detection?.suggestedWorksheetIndex ?? 0
                  ]
              )}
              onChange={(o) =>
                setCfg({ worksheet: (o as SelectOption)?.value })
              }
            />
          </Field>
          <Field>
            <Input
              label="Header row"
              value={s(cfgObj.headerRow)}
              placeholder={`suggested: ${detection?.suggestedHeaderRow ?? 1}`}
              onChange={(v: string) =>
                setCfg({ headerRow: v ? Number(v) : undefined })
              }
            />
          </Field>
          <Field>
            <Input
              label="Data starts on row (optional)"
              value={s(cfgObj.dataStartRow)}
              placeholder={`suggested: ${
                (detection?.suggestedHeaderRow ?? 1) + 1
              }`}
              onChange={(v: string) =>
                setCfg({ dataStartRow: v ? Number(v) : undefined })
              }
            />
            <Help>
              Adjust if your headers sit below title or instruction rows.
            </Help>
          </Field>
        </>
      )}

      {chosen && (fmt === "json" || fmt === "xml") && (
        <>
          <SubLabel>
            {fmt === "json" ? "JSON structure" : "XML structure"}
          </SubLabel>
          <Field>
            <Input
              label={
                fmt === "json"
                  ? "Record path (dot path; blank = the whole document is one record)"
                  : "Record element path (e.g. metadata.record)"
              }
              value={s(cfgObj.recordPath)}
              placeholder={
                s(detection?.suggestedRecordPath) ||
                (fmt === "json" ? "(document root)" : s(detection?.root))
              }
              onChange={(v: string) => setCfg({ recordPath: v })}
              clearable
            />
            {detection?.suggestedRecordPath && (
              <Help>
                Suggested record path: {detection.suggestedRecordPath}.
              </Help>
            )}
          </Field>
        </>
      )}

      {chosen && columns.isError && (
        <div style={{ marginTop: 14 }}>
          <Banner error="Could not read this source from iconik." />
        </div>
      )}

      <InlineAction>
        <Button
          label="Download CSV Template"
          icon={Download}
          type="default"
          disabled={!columns.data?.columns.length}
          onClick={() =>
            downloadCsv(
              `${cfg.data?.name ?? "workflow"}-template.csv`,
              buildCsvTemplate((columns.data?.columns ?? []).map((c) => c.name))
            )
          }
        />
      </InlineAction>
    </>
  );
}

const WDetails = styled.details`
  margin-top: 12px;
  font-size: 13px;
  color: var(--miInkSoft);
  summary {
    cursor: pointer;
    color: var(--miAccentBright);
  }
  ul {
    margin: 10px 0 0;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 220px;
    overflow: auto;
  }
  code {
    color: var(--miInkFaint);
    font-size: 11px;
  }
`;

/** Bounded, expandable list of source parse notices (empty-header normalization, etc.). */
export function WarningsDetails({
  warnings,
}: {
  warnings: { location?: string; message: string }[];
}) {
  return (
    <WDetails>
      <summary>
        {warnings.length} parse notice{warnings.length === 1 ? "" : "s"} —
        source normalization
      </summary>
      <ul>
        {warnings.map((w, i) => (
          <li key={i}>
            {w.message}
            {w.location ? <code> · {w.location}</code> : null}
          </li>
        ))}
      </ul>
    </WDetails>
  );
}

/* --------------------------------------------------------------- FIELDS editor */
type RowState = MappingRowState;

/** Instant configured/ignored/invalid signal on a mapping row (Spotter mode-badge analog). */
const StateBadge = styled.span<{ $tone: "mapped" | "invalid" }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: none;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 1px 7px 1px 6px;
  border-radius: 5px;
  vertical-align: middle;
  color: ${(p) =>
    p.$tone === "invalid" ? "var(--miDanger)" : "var(--miAccentBright)"};
  background: ${(p) =>
    p.$tone === "invalid" ? "var(--miDangerSoft)" : "var(--miAccentSoft)"};
  border: 1px solid
    ${(p) =>
      p.$tone === "invalid" ? "var(--miDangerLine)" : "var(--miAccentLine)"};
`;

/** Per-field multi-value separator — a small inline popover on the mapping's detail line (reads
 *  "Separator: Comma"), never a full-height Select row. Keyboard-accessible; MI-blue hover/focus. */
const SepWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  > .lbl {
    font-size: 12px;
    color: var(--miInkFaint);
  }
  > .pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--miRaised);
    border: 1px solid var(--miLine2);
    border-radius: 6px;
    padding: 2px 6px;
    cursor: pointer;
    font: inherit;
    font-size: 12px;
    color: var(--miInkSoft);
    line-height: 1.4;
  }
  > .pill svg {
    color: var(--miInkFaint);
  }
  > .pill:hover,
  > .pill:focus-visible {
    border-color: var(--miAccent);
    color: #fff;
  }
  > .pill:hover svg,
  > .pill:focus-visible svg {
    color: var(--miAccentBright);
  }
  > .menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 30;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 132px;
    padding: 5px;
    background: var(--miSurface);
    border: 1px solid var(--miLine);
    border-radius: 8px;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
  }
  > .menu button {
    text-align: left;
    background: none;
    border: none;
    border-radius: 5px;
    padding: 5px 8px;
    cursor: pointer;
    font: inherit;
    font-size: 12.5px;
    color: var(--miInkSoft);
  }
  > .menu button:hover,
  > .menu button:focus-visible {
    background: var(--miAccentHover);
    color: #fff;
  }
  > .menu button.on {
    color: var(--miAccentBright);
  }
`;

function SeparatorControl({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const label =
    DELIMITER_PRESETS.find((p) => p.value === value)?.label ?? "Custom";
  return (
    <ClickOutside onClickOutside={() => setOpen(false)}>
      <SepWrap>
        <span className="lbl">Separator:</span>
        <button
          type="button"
          className="pill"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {label}
          <ChevronDown size={12} />
        </button>
        {open && (
          <div className="menu">
            {DELIMITER_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                className={p.value === value ? "on" : ""}
                onClick={() => {
                  onChange(p.value);
                  setOpen(false);
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </SepWrap>
    </ClickOutside>
  );
}

const ExactBadge = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #052e1a;
  background: var(--miAccentBright);
  border-radius: 5px;
  padding: 1px 6px;
  margin-right: 7px;
`;

/** The underlying iconik field key, shown subtly next to the human label so two fields that share a
 *  label can be told apart. Never dominates the label. */
const Key = styled.code`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11.5px;
  color: var(--miInkFaint);
  background: var(--miRaised);
  border: 1px solid var(--miLine);
  border-radius: 4px;
  padding: 0 5px;
  margin-left: 6px;
`;

/* Above this many allowed values the inline chip list is replaced by a bounded, searchable viewer. */
const ALLOWED_INLINE_MAX = 20;

/* Multi-value separator choices. The persisted model keeps a single source-level `valueDelimiter`. */
const DELIMITER_PRESETS: { value: string; label: string }[] = [
  { value: ",", label: "Comma" },
  { value: "|", label: "Pipe" },
  { value: ";", label: "Semicolon" },
  { value: "\n", label: "Line break" },
];

const AvOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 60;
`;
const AvCard = styled.div`
  background: var(--miSurface);
  border: 1px solid var(--miLine);
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 560px;
  max-height: 82vh;
  display: flex;
  flex-direction: column;
  padding: 20px 22px;
`;
const AvHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  strong {
    color: #fff;
    font-size: 15px;
  }
  span {
    color: var(--miInkFaint);
    font-size: 12.5px;
  }
`;
const AvList = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  overflow: auto;
  padding: 2px;
  align-content: flex-start;
`;

/** Bounded, searchable viewer for a large controlled vocabulary. The backend stays authoritative
 *  for validation against the FULL vocabulary; this only bounds how much is rendered at once. */
function AllowedValuesModal({
  field,
  values,
  onClose,
}: {
  field: string;
  values: string[];
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const nq = q.trim().toLowerCase();
  const filtered = nq
    ? values.filter((v) => v.toLowerCase().includes(nq))
    : values;
  const CAP = 500;
  const shown = filtered.slice(0, CAP);
  return (
    <AvOverlay onClick={onClose}>
      <AvCard onClick={(e) => e.stopPropagation()}>
        <AvHead>
          <div>
            <strong>{field}</strong>
            <span>
              {" "}
              · {values.length.toLocaleString()} allowed value
              {values.length === 1 ? "" : "s"}
            </span>
          </div>
          <Button label="Close" type="default" onClick={onClose} />
        </AvHead>
        <div style={{ marginBottom: 12 }}>
          <Input
            value={q}
            onChange={setQ}
            placeholder="Filter values…"
            clearable
          />
        </div>
        <AvList>
          {shown.map((v) => (
            <Chip key={v}>{v}</Chip>
          ))}
          {!shown.length && <Help>No values match “{q}”.</Help>}
        </AvList>
        {filtered.length > CAP && (
          <Help>
            Showing the first {CAP.toLocaleString()} of{" "}
            {filtered.length.toLocaleString()} matches — refine the filter to
            narrow.
          </Help>
        )}
      </AvCard>
    </AvOverlay>
  );
}

function FieldPicker({
  catalog,
  column,
  row,
  sourceDelimiter,
  onChange,
}: {
  catalog: FieldDescriptor[];
  column: string;
  row: RowState;
  sourceDelimiter: string;
  onChange: (r: RowState) => void;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [showAllowed, setShowAllowed] = useState(false);
  const [showAvModal, setShowAvModal] = useState(false);
  const anchor = useAnchoredPosition(open);
  const d = row.descriptor;
  const ranked = useMemo(
    () => rankFields(catalog, column, q),
    [catalog, column, q]
  );
  const shown = ranked.slice(0, 60);
  const mapped = !!row.targetFieldName;
  const invalid = mapped && !!d?.readOnly;
  const effDelim = row.valueDelimiter ?? sourceDelimiter;
  return (
    <tr>
      <td
        style={{
          maxWidth: 260,
          borderLeft: `2px solid ${
            invalid
              ? "var(--miDanger)"
              : mapped
              ? "var(--miAccent)"
              : "transparent"
          }`,
          paddingLeft: 12,
        }}
      >
        {/* Name + state on ONE deterministic line: the name truncates (full text in the tooltip)
            and the badge never shrinks or wraps below. */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            title={column}
            style={{
              fontWeight: 600,
              color: "var(--miInk)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {column}
          </span>
          {invalid ? (
            <StateBadge $tone="invalid">Invalid</StateBadge>
          ) : mapped ? (
            <StateBadge $tone="mapped">
              <Check size={11} strokeWidth={3} />
              Mapped
            </StateBadge>
          ) : null}
        </div>
      </td>
      <td style={{ minWidth: 280, verticalAlign: "top" }}>
        <ClickOutside onClickOutside={() => setOpen(false)}>
          <div ref={anchor.ref} style={{ position: "relative" }}>
            <div onClick={() => setOpen(true)}>
              <Input
                value={q}
                onChange={(v: string) => {
                  setQ(v);
                  setOpen(true);
                }}
                placeholder={
                  row.targetFieldName || "click to choose an iconik field…"
                }
                noPadding
                clearable
              />
            </div>
            {open && catalog.length > 0 && (
              <PickerMenu style={anchor.style}>
                {shown.map((f) => (
                  <Pick
                    key={f.name}
                    $on={f.name === row.targetFieldName}
                    onClick={() => {
                      onChange({
                        ...row,
                        targetFieldName: f.name,
                        descriptor: f,
                      });
                      setQ("");
                      setOpen(false);
                    }}
                  >
                    {matchStrength(f, column) === "name" && (
                      <ExactBadge>Exact match</ExactBadge>
                    )}
                    {f.label}
                    {f.name !== f.label && <Key>{f.name}</Key>}{" "}
                    <span style={{ opacity: 0.55 }}>
                      · {f.fieldType}
                      {f.multi ? " · multi" : ""}
                      {f.readOnly ? " · read-only" : ""}
                    </span>
                  </Pick>
                ))}
                {ranked.length > shown.length && (
                  <div
                    style={{
                      padding: "4px 10px",
                      fontSize: 12,
                      color: "var(--miInkFaint)",
                    }}
                  >
                    {ranked.length - shown.length} more — type to filter
                  </div>
                )}
              </PickerMenu>
            )}
          </div>
        </ClickOutside>
        {row.targetFieldName && (
          <>
            {/* Detail line — ONE line: the descriptor truncates (min-width:0) so the separator and
                allowed-values action never wrap onto a third line. Full descriptor in the tooltip. */}
            <div
              style={{
                marginTop: 6,
                fontSize: 12.5,
                color: "var(--miInkFaint)",
                lineHeight: 1.5,
                display: "flex",
                flexWrap: "nowrap",
                alignItems: "center",
                gap: "0 12px",
                minWidth: 0,
              }}
            >
              <span
                title={`${d?.label ?? row.targetFieldName}${
                  d ? ` · ${d.fieldType}${d.multi ? " · multi" : ""}` : ""
                }${d?.readOnly ? " · READ-ONLY (not writable)" : ""}`}
                style={{
                  flex: "1 1 auto",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                → {d?.label ?? row.targetFieldName}
                {d && d.name !== d.label && <Key>{d.name}</Key>}
                {d
                  ? ` · ${d.fieldType}${d.multi ? " · multi" : ""}${
                      d.readOnly ? " · READ-ONLY" : ""
                    }`
                  : ""}
                {d?.readOnly && (
                  <span style={{ color: "var(--miDanger)" }}>
                    {" "}
                    (not writable)
                  </span>
                )}
              </span>
              {d?.multi && (
                <span style={{ flex: "none" }}>
                  <SeparatorControl
                    value={effDelim}
                    onChange={(v) => onChange({ ...row, valueDelimiter: v })}
                  />
                </span>
              )}
              {d && d.allowedValues.length > 0 && (
                <button
                  style={{
                    flex: "none",
                    background: "none",
                    border: "none",
                    color: "var(--miAccentBright)",
                    cursor: "pointer",
                    padding: 0,
                    font: "inherit",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() =>
                    d.allowedValues.length > ALLOWED_INLINE_MAX
                      ? setShowAvModal(true)
                      : setShowAllowed((v) => !v)
                  }
                >
                  {showAllowed && d.allowedValues.length <= ALLOWED_INLINE_MAX
                    ? "hide values"
                    : `values (${d.allowedValues.length.toLocaleString()})`}
                </button>
              )}
            </div>
            {showAllowed &&
              d &&
              d.allowedValues.length <= ALLOWED_INLINE_MAX && (
                <div
                  style={{
                    marginTop: 6,
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  {d.allowedValues.map((v) => (
                    <Chip key={v}>{v}</Chip>
                  ))}
                </div>
              )}
          </>
        )}
        {showAvModal && d && (
          <AllowedValuesModal
            field={d.label}
            values={d.allowedValues}
            onClose={() => setShowAvModal(false)}
          />
        )}
      </td>
      <td style={{ minWidth: 160 }}>
        <Select
          options={[
            { value: "append", label: "Append" },
            { value: "overwrite", label: "Overwrite" },
          ]}
          value={row.strategy}
          onChange={(o) =>
            onChange({
              ...row,
              strategy:
                ((o as SelectOption)?.value as "append" | "overwrite") ??
                "append",
            })
          }
          noPadding
        />
      </td>
      <td style={{ whiteSpace: "nowrap" }}>
        {row.targetFieldName ? (
          <span title="Clear mapping removes the current Iconik field mapping. This source column will be skipped when the workflow runs.">
            <Button
              label="Clear mapping"
              type="default"
              onClick={() =>
                onChange({ targetFieldName: "", strategy: row.strategy })
              }
            />
          </span>
        ) : (
          <Chip>unmapped</Chip>
        )}
      </td>
    </tr>
  );
}

const normMappings = (
  rows: [string, RowState][]
): {
  sourceColumn: string;
  targetFieldName: string;
  strategy: string;
  valueDelimiter: string;
}[] =>
  rows
    .map(([sourceColumn, r]) => ({
      sourceColumn,
      targetFieldName: r.targetFieldName,
      strategy: r.strategy,
      valueDelimiter: r.valueDelimiter ?? "",
    }))
    .sort((a, b) => a.sourceColumn.localeCompare(b.sourceColumn));

export function FieldsEditor({ id }: { id: string }) {
  const cfg = useProfileConfig(id);
  const columns = useSourceColumns(
    cfg.data?.source.assetId || undefined,
    cfg.data?.source.format,
    cfg.data?.source.adapterConfig
  );
  const catalog = useFieldCatalog();
  const byName = useMemo(
    () => new Map((catalog.data ?? []).map((f) => [f.name, f])),
    [catalog.data]
  );
  const [rows, setRows] = useState<Record<string, RowState> | null>(null);
  const [q, setQ] = useState("");

  // Reset draft mapping state the instant the workflow identity changes — the parent already
  // remounts this subtree on switch, but resetting here too guarantees a stale row from another
  // workflow can never be looked up against this one's columns (the `descriptor` crash). Adjusting
  // state during render is React's supported pattern for "reset state when a prop changes".
  const [seenId, setSeenId] = useState(id);
  if (seenId !== id) {
    setSeenId(id);
    setRows(null);
    setQ("");
  }

  const conf = cfg.data;
  const sourceDelim = conf?.source.valueDelimiter ?? ",";
  const cols = columns.data?.columns.map((c) => c.name) ?? [];
  const state: Record<string, RowState> =
    rows ??
    Object.fromEntries(
      cols.map((c) => {
        const m = conf?.mappings.find((x) => x.sourceColumn === c);
        return [
          c,
          {
            targetFieldName: m?.targetFieldName ?? "",
            strategy: m?.strategy ?? "append",
            valueDelimiter: m?.valueDelimiter,
            descriptor: m?.targetFieldName
              ? byName.get(m.targetFieldName)
              : undefined,
          } as RowState,
        ];
      })
    );
  const mapped = Object.entries(state).filter(([, r]) => r.targetFieldName);
  const readOnlyErr = Object.values(state).some(
    (r) => r.descriptor?.readOnly && r.targetFieldName
  );

  const dirty =
    !!conf &&
    JSON.stringify(normMappings(mapped)) !==
      JSON.stringify(
        normMappings(
          conf.mappings.map((m) => [
            m.sourceColumn,
            {
              targetFieldName: m.targetFieldName,
              strategy: m.strategy,
              valueDelimiter: m.valueDelimiter,
            },
          ])
        )
      );

  useSaveSection("fields", {
    order: 2,
    label: "Field mappings",
    dirty,
    validate: () =>
      readOnlyErr
        ? "A mapped field is read-only in iconik — pick a writable field or ignore the column."
        : null,
    getPatch: () => ({
      mappings: mapped.map(([sourceColumn, r]) => ({
        sourceColumn,
        targetFieldName: r.targetFieldName,
        strategy: r.strategy,
        // Only multi-value fields carry a per-mapping separator; others omit it.
        ...(r.descriptor?.multi && r.valueDelimiter
          ? { valueDelimiter: r.valueDelimiter }
          : {}),
      })),
    }),
  });

  const loading = Loading({ q: cfg });
  if (loading) {
    return loading;
  }
  const conf2 = conf as WireProfile;

  if (!conf2.source.assetId) {
    return (
      <Banner error="Select a source asset in Metadata Source first — mappings connect its detected columns to iconik fields." />
    );
  }

  const setRow = (col: string, r: RowState) => setRows({ ...state, [col]: r });

  const fieldRefRows: FieldRefRow[] = mapped.map(([sourceColumn, r]) => ({
    sourceColumn,
    iconikField: r.targetFieldName,
    fieldType: r.descriptor?.fieldType ?? "",
    multiValue: r.descriptor?.multi ?? false,
    strategy: r.strategy,
    valueDelimiter: r.valueDelimiter ?? sourceDelim,
    allowedValues: r.descriptor?.allowedValues ?? [],
  }));

  // A wide source (XMP, large workbooks) is configured selectively: by default only mapped fields
  // show; searching reveals matching source fields to configure. A small source shows everything.
  const LARGE = cols.length > 12;
  const nq = q.trim().toLowerCase();
  const visibleCols = cols.filter((c) => {
    if (nq) {
      return c.toLowerCase().includes(nq);
    }
    return LARGE ? !!state[c].targetFieldName : true;
  });
  const mappedCount = mapped.length;

  return (
    <>
      {columns.isFetching && <Help>Reading source…</Help>}
      {(LARGE || cols.length > 6) && (
        <SearchRow>
          <div className="box">
            <Input
              value={q}
              onChange={setQ}
              placeholder="Search source fields…"
              clearable
            />
          </div>
          <span className="hint">
            {nq
              ? `${visibleCols.length} of ${cols.length} fields match`
              : LARGE
              ? `${mappedCount} mapped of ${cols.length} — search to configure more`
              : `${cols.length} source fields`}
          </span>
        </SearchRow>
      )}
      <MapScroll>
        <HtmlTable>
          <thead>
            <tr>
              <th>Source column</th>
              <th>iconik field</th>
              <th>Strategy</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visibleCols.map((c) => (
              <FieldPicker
                key={c}
                catalog={catalog.data ?? []}
                column={c}
                // Never hand FieldPicker an undefined row (see resolveRow) — the identity-reset above
                // already prevents the cross-workflow case that produced the `descriptor` crash.
                row={resolveRow(state, c)}
                sourceDelimiter={sourceDelim}
                onChange={(r) => setRow(c, r)}
              />
            ))}
            {visibleCols.length === 0 && (
              <tr>
                <td colSpan={4} style={{ color: "var(--miInkFaint)" }}>
                  {nq
                    ? "No source fields match your search."
                    : "No fields mapped yet — search to find and configure source fields."}
                </td>
              </tr>
            )}
          </tbody>
        </HtmlTable>
      </MapScroll>
      {readOnlyErr && (
        <div style={{ marginTop: 14 }}>
          <Banner error="One or more mapped fields are read-only in iconik and cannot be written — pick a writable field or ignore the column." />
        </div>
      )}
      <Help>
        <b>Clear mapping</b> removes a row's Iconik field mapping — that source
        column is then skipped when the workflow runs.
      </Help>

      <InlineAction>
        <Button
          label="Download Field Reference"
          icon={Download}
          type="default"
          disabled={mapped.length === 0}
          onClick={() =>
            downloadCsv(
              `${conf2.name}-field-reference.csv`,
              buildFieldReferenceCsv(fieldRefRows)
            )
          }
        />
      </InlineAction>
    </>
  );
}

/* --------------------------------------------------------------- TARGET editor */
const CollList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;
/** A suggested-from-source collection — click to add it to the target list. */
const SuggestChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 9px 6px 11px;
  border-radius: 8px;
  background: var(--miRaised);
  border: 1px dashed var(--miAccentLine);
  color: var(--miInkSoft);
  font: inherit;
  font-size: 13.5px;
  cursor: pointer;

  svg.folder {
    color: var(--miAccentBright);
    flex: none;
  }
  svg.plus {
    color: var(--miInkFaint);
    flex: none;
  }
  &:hover,
  &:focus-visible {
    border-style: solid;
    border-color: var(--miAccent);
    color: #fff;
  }
  &:hover svg.plus,
  &:focus-visible svg.plus {
    color: var(--miAccentBright);
  }
`;

const CollChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 6px 8px 6px 11px;
  border-radius: 8px;
  background: var(--miAccentSoft);
  border: 1px solid var(--miAccentLine);
  color: #fff;
  font-size: 13.5px;

  svg.folder {
    color: var(--miAccentBright);
    flex: none;
  }
  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 20px;
    height: 20px;
    border: none;
    border-radius: 5px;
    background: none;
    color: var(--miInkFaint);
    cursor: pointer;
    &:hover {
      color: #fff;
      background: var(--miLine2);
    }
  }
`;

/** One selected target collection — resolves its human name (its own hook, so the list can hold a
 *  variable number of chips) and offers removal. */
function CollectionChip({
  id,
  onRemove,
}: {
  id: string;
  onRemove: () => void;
}) {
  const title = useCollectionTitle(id);
  return (
    <CollChip title={`iconik collection ${id}`}>
      <Folder size={15} className="folder" />
      <span className="name">{title.data?.title || id}</span>
      <button type="button" aria-label="Remove collection" onClick={onRemove}>
        <X size={14} />
      </button>
    </CollChip>
  );
}

export function TargetEditor({ id }: { id: string }) {
  const cfg = useProfileConfig(id);
  const [q, setQ] = useState("");
  const [ids, setIds] = useState<string[] | undefined>();
  const [descendants, setDescendants] = useState<boolean>();
  const results = useSearchCollections(q);
  const sourceColls = useSourceCollections(
    cfg.data?.source.assetId || undefined
  );

  const savedIds = cfg.data ? collectionRoots(cfg.data.targeting) : [];
  const savedDesc = cfg.data?.targeting.includeDescendants ?? true;
  const chosenIds = ids ?? savedIds;
  const inclDesc = descendants ?? savedDesc;
  const dirty =
    !!cfg.data &&
    (JSON.stringify(chosenIds) !== JSON.stringify(savedIds) ||
      inclDesc !== savedDesc);

  useSaveSection("target", {
    order: 3,
    label: "Applies to",
    dirty,
    validate: () =>
      chosenIds.length
        ? null
        : "Select at least one target collection before saving.",
    getPatch: () => ({
      targeting: {
        mode: "collection-sync",
        collectionIds: chosenIds,
        includeDescendants: inclDesc,
      },
    }),
  });

  const loading = Loading({ q: cfg });
  if (loading) {
    return loading;
  }

  const add = (cid: string) => {
    if (!chosenIds.includes(cid)) {
      setIds([...chosenIds, cid]);
    }
    setQ(""); // clear the search after a successful add
  };
  const remove = (cid: string) => setIds(chosenIds.filter((c) => c !== cid));

  return (
    <>
      <Help style={{ marginTop: 0, marginBottom: 16 }}>
        The collections and assets this workflow updates. Add one or more roots
        — overlapping assets are updated once.
      </Help>
      {chosenIds.length > 0 && (
        <CollList>
          {chosenIds.map((cid) => (
            <CollectionChip key={cid} id={cid} onRemove={() => remove(cid)} />
          ))}
        </CollList>
      )}
      {(() => {
        const suggestions = (sourceColls.data ?? []).filter(
          (c) => c.id && !chosenIds.includes(c.id)
        );
        if (suggestions.length === 0) {
          return null;
        }
        return (
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "var(--miInkFaint)",
                marginBottom: 8,
              }}
            >
              Suggested from source
            </div>
            <CollList style={{ marginBottom: 0 }}>
              {suggestions.map((c) => (
                <SuggestChip
                  key={c.id}
                  type="button"
                  onClick={() => add(c.id as string)}
                  title={`Add ${c.title}`}
                >
                  <Folder size={14} className="folder" />
                  {c.title}
                  <Plus size={14} className="plus" />
                </SuggestChip>
              ))}
            </CollList>
          </div>
        );
      })()}
      <Field>
        <Input
          label={
            chosenIds.length ? "Add another collection" : "Search collections"
          }
          value={q}
          onChange={setQ}
          placeholder="type to search collections…"
          clearable
        />
        {results.isFetching && <Help>Searching…</Help>}
        {(results.data ?? []).length > 0 && (
          <Results>
            {(results.data ?? [])
              .filter((c) => !chosenIds.includes(c.id))
              .map((c) => (
                <Pick key={c.id} onClick={() => add(c.id)}>
                  {c.title}
                </Pick>
              ))}
          </Results>
        )}
      </Field>

      <SubLabel>Scope</SubLabel>
      <Field>
        <Checkbox
          value={inclDesc}
          onChange={(v: boolean) => setDescendants(v)}
          label="Include descendant collections"
        />
        <Help>
          Applies across the selected collections plus eligible assets in their
          descendants. The source file is always excluded from the targets it
          updates.
        </Help>
      </Field>
    </>
  );
}

/* --------------------------------------------------------------- SCHEDULE + STATUS editor */
const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/** The full IANA zone list from the platform (dependency-free). Falls back to a small curated set on
 *  the rare engine without `Intl.supportedValuesOf`. There is intentionally NO auto-selected default. */
function listTimezones(): string[] {
  const withSupported = Intl as unknown as {
    supportedValuesOf?: (k: string) => string[];
  };
  if (typeof withSupported.supportedValuesOf === "function") {
    try {
      return withSupported.supportedValuesOf("timeZone");
    } catch {
      /* fall through to the curated list */
    }
  }
  return [
    "UTC",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "Europe/London",
    "Europe/Paris",
    "Asia/Tokyo",
    "Australia/Sydney",
  ];
}

/** The operator's browser zone — offered as a labeled convenience hint only, never auto-applied. */
function detectedTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

function isValidTimezone(tz: string | undefined): boolean {
  if (!tz) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Client-side mirror of the server schedule validation, so a save that would be rejected surfaces
 *  inline instead of a round-trip 422. */
function validateScheduleClient(s: {
  mode: string;
  at?: string;
  weekday?: string;
  timezone?: string;
}): string | null {
  if (s.mode === "manual") {
    return null;
  }
  if (!s.at || !/^\d{1,2}:\d{2}$/.test(s.at)) {
    return "Enter a valid execution time (HH:MM).";
  }
  if (!isValidTimezone(s.timezone)) {
    return "Select a valid timezone for a scheduled workflow.";
  }
  if (s.mode === "weekly" && !WEEKDAYS.includes(s.weekday ?? "")) {
    return "Select a weekday for a weekly schedule.";
  }
  return null;
}

export function ScheduleEditor({ id }: { id: string }) {
  const cfg = useProfileConfig(id);
  const [mode, setMode] = useState<string>();
  const [at, setAt] = useState<string>();
  const [weekday, setWeekday] = useState<string>();
  const [timezone, setTimezone] = useState<string>();

  const conf = cfg.data;
  const m = mode ?? conf?.schedule.mode ?? "manual";
  const tz = timezone ?? conf?.schedule.timezone ?? "";
  const effAt = at ?? conf?.schedule.at;
  const effWeekday = weekday ?? conf?.schedule.weekday;

  const scheduleDirty =
    !!conf &&
    (m !== conf.schedule.mode ||
      effAt !== conf.schedule.at ||
      effWeekday !== conf.schedule.weekday ||
      (tz || undefined) !== conf.schedule.timezone);

  useSaveSection("schedule", {
    order: 4,
    label: "Schedule",
    dirty: scheduleDirty,
    validate: () =>
      scheduleDirty
        ? validateScheduleClient({
            mode: m,
            at: effAt,
            weekday: effWeekday,
            timezone: tz || undefined,
          })
        : null,
    getPatch: () =>
      scheduleDirty
        ? {
            schedule: {
              mode: m as "manual" | "daily" | "weekly",
              at: effAt,
              weekday: effWeekday,
              timezone: tz || undefined,
            },
          }
        : {},
  });

  const loading = Loading({ q: cfg });
  if (loading) {
    return loading;
  }
  const detected = detectedTimezone();

  return (
    <>
      <Field>
        <Select
          label="Mode"
          options={[
            { value: "manual", label: "Manual only" },
            { value: "daily", label: "Scheduled · daily" },
            { value: "weekly", label: "Scheduled · weekly" },
          ]}
          value={m}
          onChange={(o) =>
            setMode(((o as SelectOption)?.value as string) ?? "manual")
          }
        />
        <Help>
          Manual workflows run only on Run Now. A scheduled cadence runs
          unattended at the time and timezone you set.
        </Help>
      </Field>
      {m !== "manual" && (
        <Field>
          <Input
            label="Time (HH:MM)"
            value={at ?? conf?.schedule.at ?? "02:00"}
            onChange={setAt}
          />
        </Field>
      )}
      {m === "weekly" && (
        <Field>
          <Select
            label="Weekday"
            options={WEEKDAYS.map((d) => ({ value: d, label: d }))}
            value={weekday ?? conf?.schedule.weekday ?? "Monday"}
            onChange={(o) =>
              setWeekday(((o as SelectOption)?.value as string) ?? "Monday")
            }
          />
        </Field>
      )}
      {m !== "manual" && (
        <Field>
          <Select
            label="Timezone (IANA)"
            options={[
              { value: "", label: "Select a timezone…" },
              ...listTimezones().map((z) => ({ value: z, label: z })),
            ]}
            value={tz}
            onChange={(o) =>
              setTimezone(((o as SelectOption)?.value as string) ?? "")
            }
          />
          <Help>
            The scheduled time is interpreted in this timezone (DST handled
            automatically).
            {detected ? ` Browser detected ${detected}.` : ""}
          </Help>
        </Field>
      )}
      <Help style={{ marginTop: m === "manual" ? 0 : 4 }}>
        Set the workflow to Active (top of the page) to run it. A paused or
        draft workflow never runs unattended; Run Now stays available when
        Active.
      </Help>
    </>
  );
}
